<template>
  <div class="core-accordion-item">
    <button
      v-tooltip="triggerTooltipValue"
      type="button"
      class="core-accordion-header"
      :aria-expanded="!isCollapsed"
      :title="$t('general.accordion_toggle')"
      @click="toggleAccordion">
      <div class="flex-grow">
        <component
          :is="headingTag"
          class="block text-lg"
          :role="headingTag === 'span' ? 'heading' : undefined"
          :aria-level="headingTag === 'span' ? 2 : undefined">
          <span v-html="title"></span>
        </component>
        <p v-if="description" class="text-sm whitespace-pre-line text-neutral-550" v-html="description"></p>
      </div>
      <div v-if="result" class="px-4 text-lg text-primary-dark">
        {{ result }}
      </div>
      <div class="mr-4 text-neutral-450">
        <IconChevronUp class="h-[24px] w-[24px] text-black" stroke="1.8" :class="{ 'rotate-180': isCollapsed }" />
      </div>
    </button>

    <Transition
      @before-enter="beforeAccordionEnter"
      @enter="accordionEnter"
      @after-enter="afterAccordionEnter"
      @before-leave="beforeAccordionLeave"
      @leave="accordionLeave"
      @after-leave="afterAccordionLeave">
      <div v-show="!isCollapsed" class="accordion-collapse">
        <div class="py-5">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { IconChevronUp } from '@tabler/icons-vue'

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    result?: string
    open?: boolean
    controlledOpen?: boolean
    triggerTooltip?: string
    headingTag?: 'span' | 'h2' | 'h3'
  }>(),
  {
    headingTag: 'span',
  }
)

const emit = defineEmits<{
  toggle: [open: boolean]
}>()

const localCollapsed = ref(!props.open)
const isCollapsed = computed(() => (props.controlledOpen ? !props.open : localCollapsed.value))
const triggerTooltipValue = computed(() => {
  if (!props.triggerTooltip) return ''
  return {
    text: props.triggerTooltip,
    theme: {
      placement: 'top',
      width: '18rem',
      padding: '0.6em 1em',
    },
  }
})

const toggleAccordion = function () {
  const nextOpen = isCollapsed.value
  emit('toggle', nextOpen)

  if (!props.controlledOpen) {
    localCollapsed.value = !localCollapsed.value
  }
}

const beforeAccordionEnter = function (element: Element) {
  const accordionElement = element as HTMLElement
  accordionElement.style.height = '0'
  accordionElement.style.opacity = '0'
}

const accordionEnter = function (element: Element, done: () => void) {
  const accordionElement = element as HTMLElement
  const contentHeight = accordionElement.scrollHeight

  requestAnimationFrame(() => {
    accordionElement.style.height = `${contentHeight}px`
    accordionElement.style.opacity = '1'
  })

  finishAccordionTransition(accordionElement, done)
}

const afterAccordionEnter = function (element: Element) {
  const accordionElement = element as HTMLElement
  accordionElement.style.height = 'auto'
}

const beforeAccordionLeave = function (element: Element) {
  const accordionElement = element as HTMLElement
  accordionElement.style.height = `${accordionElement.scrollHeight}px`
  accordionElement.style.opacity = '1'
}

const accordionLeave = function (element: Element, done: () => void) {
  const accordionElement = element as HTMLElement

  requestAnimationFrame(() => {
    accordionElement.style.height = '0'
    accordionElement.style.opacity = '0'
  })

  finishAccordionTransition(accordionElement, done)
}

const afterAccordionLeave = function (element: Element) {
  const accordionElement = element as HTMLElement
  accordionElement.style.height = ''
  accordionElement.style.opacity = ''
}

const finishAccordionTransition = function (accordionElement: HTMLElement, done: () => void) {
  const timeout = window.setTimeout(() => {
    cleanup()
    done()
  }, 260)

  const onTransitionEnd = function (event: TransitionEvent) {
    if (event.target !== accordionElement || event.propertyName !== 'height') return

    cleanup()
    done()
  }

  const cleanup = function () {
    window.clearTimeout(timeout)
    accordionElement.removeEventListener('transitionend', onTransitionEnd)
  }

  accordionElement.addEventListener('transitionend', onTransitionEnd)
}
</script>

<style lang="postcss" scoped>
.core-accordion-item {
  @apply border-b border-neutral-300 py-4;
  .accordion-collapse {
    @apply overflow-hidden;
    transition:
      height 220ms cubic-bezier(0.42, 0, 0.58, 1),
      opacity 220ms cubic-bezier(0.42, 0, 0.58, 1);
  }
}
.core-accordion-header {
  @apply flex w-full cursor-pointer items-center justify-between text-left;

  &:hover {
    @apply text-neutral-900;
  }

  svg {
    @apply transition-transform duration-200 ease-in-out;
  }
}
</style>
